import {
  Button,
  SelectOutlinedCreateable,
  TextOutlinedInput,
} from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import { useTexts } from '../hooks/useTexts';
import {
  IdentificationTypeAsOption,
  IdentificationTypesAsOptions,
} from '../types';
import { useAttendancesViewFetch } from './AttendancesViewLoader';
import { useState, useCallback } from 'react';
import { IdentificationDocumentCode } from '../../../../utils/enums/identification-document-code.enum';
import { useValidations } from '../../../../hooks/useValidations';
import { useParameters } from '../../../../hooks/useParameters';

type Props = {
  identificationTypes: IdentificationTypesAsOptions;
  setSearched: (value: boolean) => void;
};

export function TeacherFilter({ identificationTypes, setSearched }: Props) {
  const { validateRut } = useValidations();
  const { countryOptions } = useParameters();
  const texts = useTexts();
  const { query, updateQuery } = useAttendancesViewFetch();
  const [
    selectedIdentificationType,
    setSelectedIdentificationType,
  ] = useState<IdentificationTypeAsOption>(identificationTypes[0]);
  const [selectedCountry, setSelectedCountry] = useState(countryOptions[0]);
  const [identification, setIdentification] = useState(query.identification);
  const [isIdNotValid, setIsIdNotValid] = useState<string | undefined>();
  const dynamicValidate = useCallback(
    (value: string) => {
      if (
        selectedIdentificationType.value === IdentificationDocumentCode.Passport
      ) {
        return undefined;
      }
      if (
        selectedIdentificationType.value === IdentificationDocumentCode.CL_RUT
      ) {
        return validateRut(value);
      }

      return undefined;
    },
    [selectedIdentificationType, validateRut],
  );

  return (
    <>
      <Row>
        <Col xs={12} sm={3}>
          <SelectOutlinedCreateable
            height={40}
            name="idType"
            label={texts.teacherFilter.idTypeLabel}
            onChange={(e) => setSelectedIdentificationType(e)}
            value={selectedIdentificationType}
            options={identificationTypes}
          />
        </Col>
        {selectedIdentificationType.value ===
          IdentificationDocumentCode.Passport && (
          <Col xs={12} sm={3}>
            <SelectOutlinedCreateable
              height={40}
              name="country"
              label={texts.teacherFilter.idTypeLabel}
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e)}
              options={countryOptions}
            />
          </Col>
        )}
        <Col xs={12} sm={3}>
          <TextOutlinedInput
            name="identification"
            label={texts.teacherFilter.identificationLabel}
            value={identification}
            rules={{
              validate: {
                require: () => isIdNotValid,
              },
            }}
            onChange={(e) => {
              setIdentification(e.target.value);
              const validatedRut = dynamicValidate(e.target.value);
              setIsIdNotValid(validatedRut);
            }}
            errorText={isIdNotValid}
          />
        </Col>
        <Col xs={12} sm={3}>
          <div className="d-flex pt-4">
            <Button
              size="md"
              className="mr-4 mt-0"
              text={texts.teacherFilter.searchButton}
              onClick={() => {
                updateQuery({
                  ...query,
                  identification: identification,
                  identificationTypeId: selectedIdentificationType.value,
                  countryId: selectedCountry.value.toString(),
                });
                setSearched(true);
              }}
              disabled={identification === '' || !!isIdNotValid}
            />
            <Button
              text={texts.teacherFilter.clearButton}
              outlined
              size="md"
              onClick={() => {
                setIdentification('');
                setSelectedIdentificationType(identificationTypes[0]);
                setSearched(false);
              }}
            />
          </div>
        </Col>
      </Row>
    </>
  );
}
